import {
  RENDERABLE_SESSION_EVENT_KINDS,
  SessionContextMention,
  SessionEvent,
  SessionEventKind,
  SessionPartKind,
  UserActionV1SessionEvent,
} from "@/mds/components/sessions/session.types";
import { MDSSessionEventsState, MDSSessionEventsAction } from "./mds-session-context.events.types";
import { MDSSessionActionTypes } from "../../mds-session-context.types";
import {
  SessionItem,
  SessionItemData,
  SessionItemKind,
} from "@/mds/components/sessions/session-item";
import type { SessionPartContext } from "@/mds/components/sessions/session-parts/session-part.types";
import {
  parseSessionPartsFromEvent,
  resolveSessionPartFallbackId,
  resolveSessionPartId,
} from "./mds-session-context.events.utils";
import { last, sortBy, uniqueId } from "lodash-es";

type UserActionContextWithProvenance = {
  mentions?: SessionContextMention[] | null;
  surface?: SessionPartContext["userActionSurface"];
  input_modality?: SessionPartContext["userActionInputModality"];
};

export const mdsSessionEventsReducer = (
  state: MDSSessionEventsState,
  currentSessionId: string | null,
  action: MDSSessionActionTypes
): MDSSessionEventsState => {
  switch (action.type) {
    case MDSSessionEventsAction.UpsertSessionEvents: {
      const existingEventMap = new Map<string, { event: SessionEvent; eventIndex: number }>();

      for (const [eventIndex, event] of state.sessionEvents.entries()) {
        existingEventMap.set(event.id, { event, eventIndex });
      }

      // Create a new array starting with existing events
      const newEvents: SessionEvent[] = [...state.sessionEvents];

      // Handle event updates/additions
      for (const upsertedEvent of action.payload) {
        const existingEventData = existingEventMap.get(upsertedEvent.id);

        if (existingEventData) {
          newEvents[existingEventData.eventIndex] = upsertedEvent;
        } else {
          newEvents.push(upsertedEvent);
        }
      }

      // Filter out any events that are not for the current session
      const filteredSessionEvents =
        currentSessionId !== null
          ? newEvents.filter(event => event.session_id === currentSessionId)
          : newEvents;

      /**
       * We sort events by the `internal_updated_at` field, which is when
       * the write hits the server.
       *
       * (This roughly matches "what the agent sees".)
       */
      const sortedEvents = sortBy(filteredSessionEvents, x => x.internal_updated_at);

      const { items: updatedItems, itemIdCache: updatedItemIdCache } =
        calculateSessionItemsFromEvents({
          events: sortedEvents,
          toggleState: state.sessionItemToggleState,
          itemIdCache: state.sessionItemIdCache,
        });

      return {
        ...state,
        sessionEvents: sortedEvents,
        sessionItems: updatedItems,
        sessionItemIdCache: updatedItemIdCache,
      };
    }

    case MDSSessionEventsAction.UpdateSessionStatus: {
      return {
        ...state,
        sessionStatus: action.payload,
      };
    }

    case MDSSessionEventsAction.ToggleSessionItemExpandedState: {
      const { itemId } = action.payload;

      const currentToggleState = state.sessionItemToggleState[itemId] ?? false;

      const updatedToggleState = {
        ...state.sessionItemToggleState,
        [itemId]: !currentToggleState,
      };

      const { items: updatedItems, itemIdCache: updatedItemIdCache } =
        calculateSessionItemsFromEvents({
          events: state.sessionEvents,
          toggleState: updatedToggleState,
          itemIdCache: state.sessionItemIdCache,
        });

      return {
        ...state,
        sessionItemToggleState: updatedToggleState,
        sessionItems: updatedItems,
        sessionItemIdCache: updatedItemIdCache,
      };
    }

    default:
      return state;
  }
};

/**
 * Transform session events to session item state.
 * Recalculates the entire session items state from events.
 */
export const calculateSessionItemsFromEvents = ({
  events: allServerEvents,
  toggleState: existingToggleState,
  itemIdCache: existingItemIdCache,
}: {
  events: SessionEvent[];
  toggleState: { [itemId: string]: boolean };
  itemIdCache: { [partId: string]: string };
}): { items: SessionItem[]; itemIdCache: { [partId: string]: string } } => {
  const items: SessionItem[] = [];
  const itemIdCache: { [partId: string]: string } = { ...existingItemIdCache };

  const renderableEvents = allServerEvents.filter(event => {
    return RENDERABLE_SESSION_EVENT_KINDS.includes(event.data.kind as SessionEventKind);
  });

  for (const event of renderableEvents) {
    const eventParts = parseSessionPartsFromEvent({ event });

    for (const [eventPartIndex, eventPart] of eventParts.entries()) {
      const partId = resolveSessionPartId({
        part: eventPart,
        fallbackPartId: resolveSessionPartFallbackId({
          eventId: event.id,
          partIndex: eventPartIndex,
        }),
      });

      // Determine the target item kind before resolving the item ID,
      // so the cache key is scoped by kind and avoids cross-kind collisions
      // (e.g. a reasoning part and standard part sharing the same partId).
      const targetItemKind = (() => {
        // We fold the "Interrupted" parts into the reasoning groups.
        if (
          eventPart.kind === SessionPartKind.ReasoningV1 ||
          eventPart.kind === SessionPartKind.InterruptV1
        ) {
          return SessionItemKind.ReasoningGroup;
        }

        return SessionItemKind.Standard;
      })();

      const cacheKey = `${targetItemKind}:${partId}`;
      const itemId = itemIdCache[cacheKey] || uniqueId("item-");

      const userActionContext =
        event.data.kind === SessionEventKind.UserActionV1
          ? ((event.data as UserActionV1SessionEvent).value.context as
              | UserActionContextWithProvenance
              | null
              | undefined)
          : undefined;
      const mentions =
        event.data.kind === SessionEventKind.UserActionV1
          ? (userActionContext?.mentions ?? [])
          : undefined;

      const itemData: SessionItemData = {
        part: eventPart,
        context: {
          eventKind: event.data.kind as SessionEventKind,
          eventId: event.id,
          partId,
          itemId,
          lastArtifactId: null,
          feedbackGiven: event.feedbackGiven,
          mentions,
          userActionSurface: userActionContext?.surface,
          userActionInputModality: userActionContext?.input_modality,
          threadReplyCount: event.thread?.replyCount,
          threadHasUserReply: event.thread?.hasUserReply,
          threadHasAgentReply: event.thread?.hasAgentReply,
        },
      };

      const lastItem = last(items);

      // Handle grouping logic for ReasoningGroup items
      if (targetItemKind === SessionItemKind.ReasoningGroup) {
        if (lastItem?.kind === SessionItemKind.ReasoningGroup) {
          const targetItemId = lastItem.id;

          items[items.length - 1].data.push({
            ...itemData,
            context: {
              ...itemData.context,
              itemId: targetItemId,
            },
          });

          itemIdCache[cacheKey] = targetItemId;
          continue;
        }
      }

      const sessionItem: SessionItem = {
        id: itemId,
        kind: targetItemKind,
        data: [itemData],
        context: {
          isExpanded: existingToggleState[itemId] || false,
        },
      };

      items.push(sessionItem);
      itemIdCache[cacheKey] = itemId;
      continue;
    }
  }

  return { items, itemIdCache };
};
